import { forwardRef, useRef, type Ref, Children } from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex, Divider } from '@dynatrace/strato-components/layouts';

import {
  AccordionActions,
  type MultiExpanded,
  type SingleExpanded,
  useAccordionState,
} from './accordion-state.js';
import { accordionDividerMarginCSS } from './Accordion.sty.js';
import { AccordionSection } from './AccordionSection.js';
import { AccordionSectionContent } from './AccordionSectionContent.js';
import { AccordionSectionLabel } from './AccordionSectionLabel.js';
import { AccordionContextProvider } from './contexts/AccordionContext.js';
//#region Typings

/** @public */
export interface AccordionOwnProps<Multiple extends boolean>
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * Determines whether multiple sections can be open at the same time.
   * @defaultValue false
   */
  multiple?: Multiple;

  /**
   * The position of the trigger that expands and collapses a section.
   * @defaultValue 'end'
   */
  triggerPosition?: 'start' | 'end';

  /**
   * Callback fired when a section is expanded/collapsed.
   */
  onExpandChange?: (value: OnChangeValue<Multiple>) => void;

  /**
   * If the accordion is interactive, the whole header is interactive and can open or close the accordion.
   * Otherwise, only the button is interactive
   * @defaultValue true
   */
  interactive?: boolean;

  /**
   * The color of the accordion.
   * Setting the color affects the button, label, content and divider color.
   * @defaultValue 'neutral'
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';

  /**
   * The size of the accordion.
   * @defaultValue 'default'
   */
  size?: 'default' | 'condensed';

  /**
   * Whether a divider is shown between the sections.
   * @defaultValue true
   */
  showDividers?: boolean;
}

/** @public */
export interface AccordionUncontrolledProps<Multiple extends boolean> {
  /**
   * Determines which sections are initially expanded in an uncontrolled scenario.
   * @defaultValue false
   */
  defaultExpanded?: Multiple extends true ? MultiExpanded : SingleExpanded;
  /**
   * Determines which sections expanded in a controlled scenario.
   */
  expanded?: never;
}

/** @public */
export interface AccordionControlledProps<Multiple extends boolean> {
  /**
   * Determines which sections are initially expanded in an uncontrolled scenario.
   */
  defaultExpanded?: never;
  /**
   * Determines which sections are expanded in a controlled scenario.
   */
  expanded: Multiple extends true ? MultiExpanded : SingleExpanded;
}

/** @public */
export type AccordionProps<Multiple extends boolean> =
  AccordionOwnProps<Multiple> &
    (AccordionControlledProps<Multiple> | AccordionUncontrolledProps<Multiple>);

/** @public */
export type OnChangeValue<Multiple extends boolean> = Multiple extends true
  ? MultiExpanded
  : SingleExpanded;
//#endregion Typings

/**
 * Accordion component
 */
function RootAccordion<Multiple extends boolean = false>(
  props: AccordionProps<Multiple>,
  forwardedRef: Ref<HTMLDivElement>,
) {
  const {
    expanded,
    defaultExpanded,
    multiple = false as Multiple,
    triggerPosition = 'end',
    interactive = true,
    color = 'neutral',
    showDividers = true,
    size = 'default',
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  }: AccordionProps<Multiple> = props;

  const sectionCount = Children.count(children);
  const accordionSections = Children.map(children, (child, index) => (
    <>
      {child}
      {showDividers && sectionCount > 1 && index < sectionCount - 1 && (
        <Divider className={accordionDividerMarginCSS[size]} color={color} />
      )}
    </>
  ));

  const [state, dispatch] = useAccordionState<Multiple>({
    expanded,
    defaultExpanded,
    multiple,
    onExpandChange: props.onExpandChange,
  });

  // Syncronize the open elements if multiple changes.
  const lastMultiple = useRef(multiple);
  if (multiple !== lastMultiple.current) {
    dispatch({ type: AccordionActions.SET_MULTIPLE, payload: multiple });
    lastMultiple.current = multiple;
  }

  return (
    <div
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
    >
      <AccordionContextProvider
        value={{
          triggerPosition,
          size,
          interactive,
          state,
          color,
          dispatch,
        }}
      >
        <Flex
          flexDirection="column"
          gap={showDividers ? 0 : size === 'default' ? 8 : 4}
        >
          {accordionSections}
        </Flex>
      </AccordionContextProvider>
    </div>
  );
}

RootAccordion.displayName = 'Accordion';

const AccordionForwardRef = forwardRef(RootAccordion);

/**
 * The `Accordion` component can be used to group information, reveal and hide content.
 * @public
 */
export const Accordion = Object.assign(AccordionForwardRef, {
  Section: AccordionSection,
  SectionLabel: AccordionSectionLabel,
  SectionContent: AccordionSectionContent,
});
